import React, {useMemo, useRef, useState, useEffect} from 'react';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';

import keyCodes, { isNavigateButton } from 'js/shared/keyCodes';
import CustomScrollbar from 'js/components/shared/CustomScrollbarRedesign';

import styles from './SelectDropdown.module.scss';

const cx = classnames.bind(styles);

const SelectDropdown = (props) => {
  const {
    className,
    options,
    searchable = true,
    value,
    position = 'bottom',
    height = 200,
    onSelect,
    onClose,
    noResultsData,
  } = props;
  const [filterQuery, setFilterQuery] = useState('');
  const dropdownScrollRef = useRef(null);
  const searchRef = useRef(null);
  const wrapRef = useRef(null);

  /**
   * Close select dropdown
   * @param focus - focus select element back on dropdown close
   */
  const dropDownClose = function (focus) {
    if (onClose) {
      onClose(focus)
    }
  }

  /**
   *
   * @param index index of focussed element.
   *              -1 - prev active element
   *              0 - active element
   *              1 - after active element
   */
  const focusElement = function (index) {
    const focussedElement = window.document.activeElement;
    let defaultFocussedElement = dropdownScrollRef?.current?.container?.querySelector('[data-selected="true"]');
    if (!defaultFocussedElement) {
      defaultFocussedElement = dropdownScrollRef?.current?.container?.querySelector('[data-selected]')
    }
    const activeElement = dropdownScrollRef?.current?.container?.contains(focussedElement)
      ? focussedElement
      : defaultFocussedElement;

    switch (index) {
      case 0:
        activeElement.focus();
        break;
      case -1:
        activeElement?.previousSibling?.focus();
        break;
      case 1:
        activeElement?.nextSibling?.focus();
        break;
    }
  }

  const scrollToActive = function () {
    const activeElement = dropdownScrollRef?.current?.container?.querySelector('[data-selected="true"]');
    if (activeElement) {
      dropdownScrollRef.current.scrollTop(activeElement.offsetTop - 40);
    }
  }

  const onSelectElement = function (e, el) {
    e.preventDefault();
    e.stopPropagation();

    onSelect && onSelect(el)
  }

  const onElementPress = function (e, el) {
    if (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.space) {
      onSelectElement(e, el);
    }
  }

  const onFilterChange = function (e) {
    setFilterQuery(e?.target?.value || '')
  }

  const onFilterClean = function (e) {
    e.preventDefault()
    e.stopPropagation()
    setFilterQuery('')
    searchRef.current.focus()
  }

  const filteredOptions = useMemo(function () {
    const results = filterQuery
      ? options.filter(el => el.label.toLowerCase().includes(filterQuery.toLowerCase()))
      : options;
    return results?.length ? results : [noResultsData];
  }, [filterQuery, options])

  const checkClickOutside = function (e) {
    const isDropdownClick = wrapRef.current === e.target || wrapRef.current.contains(e.target)
    if (!isDropdownClick) {
      e.preventDefault();
      e.stopPropagation();
      dropDownClose(false)
    }
  }

  const checkFocusOutside = function () {
    setTimeout(function () {
      if (wrapRef?.current) {
        const isDropdownFocussed = wrapRef.current === document.activeElement || wrapRef.current.contains(document.activeElement)
        if (!isDropdownFocussed) {
          dropDownClose(false);
        }
      }
    }, 200)
  }

  const focusSearch = function () {
    searchRef.current.focus()
  }

  const checkClickKeyDown = function (e) {

    if (e.keyCode === keyCodes.arrowUp || e.keyCode === keyCodes.arrowDown) {
      e.preventDefault();
      focusElement(e.keyCode === keyCodes.arrowUp ? -1 : 1)
    }

    if (e.keyCode === keyCodes.esc) {
      dropDownClose(true)
      return;
    }

    if (searchable && !isNavigateButton(e.keyCode)) {
      focusSearch()
    }
  }

  useEffect(function () {
    if (searchable) {
      focusSearch()
    } else {
      focusElement(0)
    }
    scrollToActive()
    window.document.addEventListener('click', checkClickOutside)
    window.document.addEventListener('keydown', checkClickKeyDown)

    return function () {
      window.document.removeEventListener('click', checkClickOutside)
      window.document.removeEventListener('keydown', checkClickKeyDown)
    }
  }, [])

  return (
    <div
      className={cx('dropdown-wrap', `position-${position}`, className)}
      ref={wrapRef}
      onBlur={checkFocusOutside}
    >
      {searchable && (
        <div className={cx('search-wrap')}>
          <input
            type="text"
            ref={searchRef}
            className={cx('search')}
            value={filterQuery}
            onChange={onFilterChange}
          />
          <button type="button" onClick={onFilterClean} className={cx('search-clean')}/>
        </div>
      )}
      <CustomScrollbar ref={dropdownScrollRef} autoHeight autoHeightMax={height}>
        <ul className={cx('options-wrap')}>
          {filteredOptions.map((el, i) => (
            <li
              className={cx('option', {
                active: el.value && (el.value === value),
                disabled: el.disabled
              })}
              data-selected={el.value && (el.value === value)}
              tabIndex={el.value && (el.value === value) ? 0 : -1}
              key={'option_' + el.value + i}
              onKeyDown={(e) => onElementPress(e, el)}
              onClick={(e) => onSelectElement(e, el)}
            >
              {el.template}
            </li>
          ))}
        </ul>
      </CustomScrollbar>
    </div>
  )
}

SelectDropdown.propTypes = {
  className: PropTypes.string,
  height: PropTypes.number,
  position: PropTypes.oneOf(['bottom', 'top']),
}

export default SelectDropdown;
